import Backbone from 'backbone'
import _ from 'lodash'
import appDisplay from 'scoreboard/components/app-display'
import diagnostics from 'scoreboard/components/diagnostics'
import spectator from 'scoreboard/components/spectator'
import NewSlideForm from 'scoreboard/briefings/new-slide-form'
import Slide from 'scoreboard/briefings/slide'
import ShareDashboard from 'scoreboard/dashboards/share-dashboard'
import SharedDashboard from 'scoreboard/dashboards/shared-dashboard'
import SharedForm from 'scoreboard/forms/shared-form'
import SharedStrategyMap from 'scoreboard/strategy-maps/shared-strategy-map'
import permissions from 'scoreboard/components/permissions'
import sendToExport from 'scoreboard/components/export/send-to-export'
import MetricValueExportWizard from 'scoreboard/scorecards/overview/metric-value-export-wizard'
import i18n from 'scoreboard/components/i18n'
import metadata from 'scoreboard/components/metadata'
import guard from 'scoreboard/components/unsaved/guard'
import user from 'scoreboard/components/user'
import license from 'scoreboard/components/license'
import licenseRestriction from './license-restriction'
import BootslideClickableMenuOption from 'scoreboard/components/bootslide/bootslide-clickable-menu-option'
import conductor from 'scoreboard/components/conductor'

// Here is where all of our send-to options should be located so that all views
// that need them can have access to them.

const sharedModels = {
	'strategy-map': SharedStrategyMap,
	'form': SharedForm,
	'dashboard': SharedDashboard
}

var SendToOption = BootslideClickableMenuOption.extend({
	_onClick: function (e) {
		e.stopPropagation()
		e.preventDefault()

		this.send()
		this.trigger('done')
	}
})

/**
 * Centralized logic for showing the "Download Now" or "Schedule Export"
 * form for all export types
 */
function exportAction(type, chopAfter, options = {}) {
	if (options.immediate) {
		return sendToExport.download.bind(sendToExport, ...arguments)
	}
	return sendToExport.prompt.bind(sendToExport, ...arguments)
}

/**
 * Send-To Menu options - each one is implemented by a function which returns
 * an array of zero or more Backbone View objects.
 *
 * Arrays are used so that they can be easily concatenated together in the event
 * certain menu options are disabled.
 *
 * Any global checks for option visibility are meant to be applied inside
 * the functions below to keep things DRY
 *
 * Options that represents exports must have a class property 'export' set to
 * true. This is used in display-header.js to filter out export only options
 * for bookmarks and briefing slides.
 *
 */
var options = {

	/**
	 * An array with default options.
	 * @param {ExportOptions} options
	 * @returns {Array}
	 */
	defaultNoExport: function (options={}) {
		return [].concat(this.briefing(options))
	},

	/**
	 * @param {ExportOptions} options
	 * @see exportServerOptions
	 */
	'default': function (options={}) {
		return this.defaultNoExport(options).concat(this.exportServerOptions(options))
	},
	/**
	 * @param {ExportOptions} options
	 */
	excel: function(options={}) {
		if (user.isStrictViewer()) return []

		return [SendToOption.extend({
			icon: 'docs-spreadsheet-mono',
			label: 'common.export.excel',
			menuLabel: 'common.export.excel',
			send: exportAction('excel', undefined /*chopAfter*/, options)
		}, {export: true})]
	},

	/**
	 * @param {ExportOptions} options
	 */
	csv: function(options={}) {
		if (user.isStrictViewer()) return []

		return [SendToOption.extend({
			icon: 'docs-spreadsheet-mono',
			label: 'common.export.csv',
			menuLabel: 'common.export.csv',
			send: exportAction('csv', undefined /*chopAfter*/, options)
		}, {export: true})]
	},

	/**
	/**
	 * Returns all of the common export options based on availability of the
	 * export server, e-mail server etc.
	 *
	 * options parameters are documented in sendToExport.prompt and
	 * apply to all export operations that take options in their signature.
	 *
	 * @param {ExportOptions} options
	 */
	exportServerOptions : function(options={}) {
		// If any of these are disabled they will simply return empty arrays
		var possible = [this.printer(options), this.email(options), this.word(options)]
		if (options.excelFn) {
			possible.push(this.excel(options))
		} else if (options.csvDownloadFn) {
			possible.push(this.csv(options))
		}
		possible.push(this.pptx(options))
		possible.push(this.pdf(options))
		return _.flatten(possible)
	},
	/**
	 * @param {ExportOptions} options
	 * @see exportServerOptions
	 */
	briefing: function (options) {
		if (user.isStrictViewer()) return []
		//not org specific
		return !permissions.anyBriefing() ? [] : [SendToOption.extend({
			icon: 'briefings',
			label: 'briefingBooks.briefing',
			menuLabel: 'briefingBooks.sendToBriefing',
			send: function () {
				guard.conditionallyRun(() => {
					var url = Backbone.history.fragment
					let spectated = spectator.getCurrent()

					if (spectated) {
						url = spectated.get('url')
					}
					var slide = new Slide({
						// heading is set on model in send-to.js and display-header.js (mobile options)
						label: _.get(this.model, 'heading', ''),
						url: url,
						uiState: JSON.stringify(_.result(options, 'uiState'))
					})
					let itemContext = _.extend({}, options)
					appDisplay.dialog(new NewSlideForm({
						model: slide,
						itemContext,
						spectated
					}))
				})
			}
		})]
	},
	sharedDashboard: function(opt = {}) {
		let section = opt.section || 'dashboards'
		let uiState = opt.uiState || {}
		let sectionMetadata = metadata[section]

		if (user.isStrictViewer()) return []
		var label = i18n.get('admin.sharedDashboards.sharedDashboard', sectionMetadata.singularCode)
		return [SendToOption.extend({
			icon: sectionMetadata.domainType,
			label,
			menuLabel: label,
			send: function () {
				let shareDashboardView
				if (license.embeddabledDashboards) {
					const domainObjectId = spectator.getCurrentId()
					var Model = sharedModels[sectionMetadata.domainType]
					var model = new Model({domainObjectId}, {uiState})
					shareDashboardView = new ShareDashboard({ model: model })
				} else {
					if (section === 'dashboards') {
						shareDashboardView = licenseRestriction.sharedDashboardRestriction()
					} else {
						// #30491 license restriction dialog with Strategy Map wording
						shareDashboardView = licenseRestriction.sharedStrategyMapRestriction()
					}
				}
				appDisplay.dialog(shareDashboardView)
			}
		})]
	},
	/**
	 * Send To option for sharing a form
	 * This was previously handled by the sharedDashboard option, because
	 * of the shared code, but I'm breaking this out to handle the
	 * license restriction for forms properly (#56253)
	 */
	sharedForm: function(opt = {}) {
		let section = opt.section || 'forms'
		let uiState = opt.uiState || {}
		let sectionMetadata = metadata[section]

		if (user.isStrictViewer()) return []
		let label = i18n.get('admin.sharedDashboards.sharedDashboard', sectionMetadata.singularCode)
		return [SendToOption.extend({
			icon: sectionMetadata.domainType,
			label,
			menuLabel: label,
			send: function () {
				let shareFormView
				if (license.forms) {
					const domainObjectId = spectator.getCurrentId()
					var Model = sharedModels[sectionMetadata.domainType]
					var model = new Model({domainObjectId}, {uiState})
					shareFormView = new ShareDashboard({ model: model })
				} else {
					shareFormView = licenseRestriction.sharedFormRestriction()
				}
				appDisplay.dialog(shareFormView)
			}
		})]
	},
	email: function(options = {}) {
		if (user.isStrictViewer()) return []
		var emailEnabled = diagnostics.exportEnabled && diagnostics.emailEnabled

		// #51022 when emailing from a bookmark, make sure we have the correct
		// information for the bookmarked domain object
		if (spectator.getCurrent() && 'bookmarks' === conductor.section()) {
			_.extend(options, {
				domainId: spectator.getCurrentId(),
				exportURL: spectator.getCurrentUrl(),
				section: conductor.section(spectator.getCurrentUrl())
			})
		}
		return !emailEnabled ? [] : [SendToOption.extend({
			icon: 'email',
			label: 'common.email',
			menuLabel: 'common.email',
			send: sendToExport.email.bind(sendToExport, options)
		}, {export: true})]
	},
	/**
	 * @param {ExportOptions} options
	 */
	printer: function (options = {}) {
		if (user.isStrictViewer()) return []
		return !diagnostics.exportEnabled ? [] : [SendToOption.extend({
			icon: 'docs-document',
			label: 'common.export.printer',
			menuLabel: 'common.export.printer',
			send: sendToExport.download.bind(sendToExport, 'pdf',
				undefined /*chopAfter*/, _.extend({forPrint: true}, options))
		}, {export: true})]
	},
	/**
	 * @param {ExportOptions} options
	 */
	pdf: function (options = {}) {
		if (user.isStrictViewer()) return []
		return !diagnostics.exportEnabled ? [] : [SendToOption.extend({
			icon: 'docs-acrobat-mono',
			label: 'common.export.pdf',
			menuLabel: 'common.export.pdf',
			send: exportAction('pdf', undefined /*chopAfter*/, options)
		}, {export: true})]
	},
	/**
	 * @param {ExportOptions} options
	 */
	pptx: function (options = {}) {
		if (user.isStrictViewer()) return []
		return !diagnostics.exportEnabled ? [] : [SendToOption.extend({
			icon: 'docs-presentation-mono',
			label: 'common.export.powerpoint',
			menuLabel: 'common.export.powerpoint',
			send: exportAction('powerpoint', undefined /*chopAfter*/, options)
		}, {export: true})]
	},
	/**
	 * @param {ExportOptions} options
	 */
	word: function (options = {}) {
		if (user.isStrictViewer()) return []
		return !diagnostics.exportEnabled ? [] : [SendToOption.extend({
			icon: 'docs-document',
			label: 'common.export.word',
			menuLabel: 'common.export.word',
			send: exportAction('word', undefined /*chopAfter*/, options)
		}, {export: true})]
	},
	png: function (options = {}) {
		if (user.isStrictViewer()) return []
		return !diagnostics.exportEnabled ? [] : [SendToOption.extend({
			icon: 'docs-image-mono',
			label: 'common.export.png',
			menuLabel: 'common.export.png',
			send: exportAction('png', undefined /*chopAfter*/, options)
		}, {export: true})]
	},
	emailAllSlides: function (options = {}) {
		if (user.isStrictViewer()) return []
		return [SendToOption.extend({
			icon: 'email',
			label: 'common.email',
			menuLabel: 'common.email',
			send: sendToExport.emailBriefing.bind(sendToExport, options)
		}, {export: true})]
	},
	printAllSlides: function (options = {}) {
		if (user.isStrictViewer()) return []
		return [SendToOption.extend({
			icon: 'docs-document',
			label: 'common.export.printer',
			menuLabel: 'common.export.printer',
			/*remove slide ID so we get all slides*/
			send: sendToExport.download.bind(sendToExport, 'pdf', '/slides',
				_.extend({forPrint: true}, options))
		}, {export: true})]
	},
	pdfAllSlides: function (options = {}) {
		if (user.isStrictViewer()) return []
		return [SendToOption.extend({
			icon: 'docs-acrobat-mono',
			label: 'common.export.pdf',
			menuLabel: 'common.export.pdf',
			/*remove slide ID so we get all slides*/
			send: exportAction('pdf', '/slides', options)
		}, {export: true})]
	},
	pptxAllSlides: function (options = {}) {
		if (user.isStrictViewer()) return []
		return [SendToOption.extend({
			icon: 'docs-presentation-mono',
			label: 'common.export.powerpoint',
			menuLabel: 'common.export.powerpoint',
			/*remove slide ID so we get all slides*/
			send: exportAction('powerpoint', '/slides', options)
		}, {export: true})]
	},
	wordAllSlides: function (options = {}) {
		if (user.isStrictViewer()) return []
		return [SendToOption.extend({
			icon: 'docs-presentation-mono',
			label: 'common.export.word',
			menuLabel: 'common.export.word',
			/*remove slide ID so we get all slides*/
			send: exportAction('word', '/slides', options)
		}, {export: true})]
	},
	scorecardStructureImport: function () {
		if (user.isStrictViewer()) return []
		return [SendToOption.extend({
			icon: 'scorecard',
			label: 'scorecards.exportStructure',
			menuLabel: 'scorecards.exportStructure',
			send: () => sendToExport.scorecardStructureImportFile()
		})]
	},
	scorecardValueImport: function () {
		if (user.isStrictViewer()) return []
		return [SendToOption.extend({
			icon: 'metric',
			label: 'scorecards.exportValues',
			menuLabel: 'scorecards.exportValues',
			send: () => new MetricValueExportWizard().start()
		})]
	}
}

export default options

options.__filename__ = 'components/send-to-options.js'
